import React from "react";
import { FileText, MapPin, TrendingUp, Flag, Navigation, AlertCircle } from "lucide-react";
import Modal from "../common/Modal";
import Button from "../common/Button";

const RouteViewModal = ({ open, onClose, route }) => {
  if (!route) return null;

  const infoItems = [
    {
      icon: FileText,
      label: "Route Name",
      value: route.name,
      color: "text-blue-600 dark:text-blue-400",
      bgColor: "bg-blue-50 dark:bg-blue-900/20",
    },
    {
      icon: TrendingUp,
      label: "Difficulty",
      value: route.difficulty,
      color: "text-orange-600 dark:text-orange-400",
      bgColor: "bg-orange-50 dark:bg-orange-900/20",
    },
    {
      icon: MapPin,
      label: "State",
      value: route.stateCode,
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-50 dark:bg-green-900/20",
    },
    {
      icon: Navigation,
      label: "Start Point",
      value: route.startPoint || "Not specified",
      color: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-50 dark:bg-purple-900/20",
    },
    {
      icon: Flag,
      label: "End Point",
      value: route.endPoint || "Not specified",
      color: "text-pink-600 dark:text-pink-400",
      bgColor: "bg-pink-50 dark:bg-pink-900/20",
    },
  ];

  return (
    <Modal open={open} onClose={onClose} title="Route Details" showCloseButton={false}>
      <div className="space-y-4">
        {/* Description */}
        {route.description && (
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {route.description}
            </p>
          </div>
        )}

        {/* Rejection Reason - Only show for rejected routes */}
        {route.status === "rejected" && route.rejectionReason && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border-l-4 border-red-500">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">
                  Rejection Reason
                </h4>
                <p className="text-sm text-red-700 dark:text-red-400">
                  {route.rejectionReason}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Route Info Grid */}
        <div className="grid grid-cols-1 gap-3">
          {infoItems.map((item, index) => (
            <div
              key={index}
              className="flex items-start gap-3 p-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg"
            >
              <div className={`p-2 ${item.bgColor} rounded-md ${item.color}`}>
                <item.icon size={18} />
              </div>
              <div className="flex-1">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {item.label}
                </div>
                <div className="font-medium text-gray-800 dark:text-gray-200 mt-0.5">
                  {item.value}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Distance if available */}
        {route.distance && (
          <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 p-4 rounded-lg border border-green-100 dark:border-green-800">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Distance</div>
            <div className="text-2xl font-bold text-gray-800 dark:text-gray-200 mt-1">
              {route.distance.toFixed(2)} km
            </div>
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RouteViewModal;
